import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import classNames from 'classnames';
import { ReactComponent as ChevronRight } from '@assets/images/chevron-right.svg';
import { ReactComponent as InfoIcon } from '@assets/images/information-circle.svg';
import Sider from '@components/Sider';
import { useAuthentication } from '@hooks/authentication';
import { useViewport } from '@hooks/viewport';
import { useNavigationBarStore } from '@stores/navigationBarStore';
import { isPartnerRole, openLink } from '@utils/common';
import Header from './Header';
import { LayoutProvider } from './LayoutContext';
import { computeLayout } from './layoutUtils';

const GlobalLayout: React.FC = () => {
  const { isMobile } = useViewport();
  const location = useLocation();
  const { isSiderOpen, closeSider, openSider } = useNavigationBarStore();

  const publicPaths = ['/join', '/login', 'auth', '/invite'];
  const isPublicPath = publicPaths.some((path) => location.pathname.startsWith(path));
  const authentication = useAuthentication({ disableLoginRefresh: false, loginRequired: !isPublicPath });

  const isPartner = isPartnerRole(authentication.authStore.user);
  // Show Sider on all non-landing pages when the nav state says it's open (regardless of role load timing)
  const hideSider = !isSiderOpen;

  const [headerProps, setHeaderProps] = React.useState<any>(undefined);

  React.useEffect(() => {
    if (isMobile || computeLayout({ pathname: location.pathname, isMobile, isPartner }).landingPage) {
      closeSider();
    } else {
      openSider();
    }
  }, [isMobile, location.pathname, isPartner, openSider, closeSider]);

  // Match previous AppLayout container styles for content area
  const { layoutHeight, landingPage, showGuideBar } = computeLayout({
    pathname: location.pathname,
    isMobile,
    isPartner,
  });

  return (
    <LayoutProvider value={{ layoutActive: true, headerProps, setHeaderProps }}>
      <Header landing={landingPage} {...headerProps} />
      {showGuideBar && (
        <div
          style={{
            height: '56px',
            //width: '100%',
            padding: '0 20px',
            background: 'white',
            borderBottom: '1px solid #e8eaed',
          }}>
          <button
            className="link-button"
            onClick={() => {
              openLink('https://igogo.notion.site/1c796a81624b80808569e863fabfab8c?pvs=4');
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '56px' }}>
              <InfoIcon />
              <div style={{ color: '#242424', textAlign: 'right', fontSize: '16px', fontWeight: 600 }}>
                파트너스가 처음이신가요?
              </div>
              <ChevronRight />
            </div>
          </button>
        </div>
      )}
      <Layout
        style={{ height: layoutHeight }}
        className={classNames('partner-admin-app ant-layout-has-sider', !authentication?.authStore?.user && 'login')}>
        {!landingPage && !hideSider && <Sider onHide={closeSider} />}
        <Layout.Content
          id="layout-content"
          style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          {isMobile && isSiderOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 10,
              }}
              onClick={closeSider}
            />
          )}
          <Outlet />
        </Layout.Content>
      </Layout>
    </LayoutProvider>
  );
};

export default GlobalLayout;
